import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, Row, View } from "@amzn/stencil-react-components/layout";
import {
  MessageBanner,
  MessageBannerType,
} from "@amzn/stencil-react-components/message-banner";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import React, { FC } from "react";
import { useHistory } from "react-router-dom";
import { EmptyState } from "@/common/components/EmptyState/empty-state";
import { buildPath, Paths } from "@/common/paths/paths";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import { useMyHardware } from "@/common/hooks/use-my-hardware.hook";
import type { MyHardwareDevice } from "@/services/my-hardware/my-hardware.types";
import { ReplacementNavState } from "@/pages/Replacement/replacement.types";
import { useReplacementDraftStore } from "@/pages/Replacement/use-replacement-draft-store";
import { DeviceGroup } from "@/pages/MyHardware/components/DeviceGroup/device-group";
import { AssetActionKind, needsAction } from "@/pages/MyHardware/my-hardware.types";

export const AssignedHardwareTab: FC = () => {
  const { data, isLoading, isError, refetch } = useMyHardware();
  const resetReplacementDraft = useReplacementDraftStore((state) => state.resetDraft);
  const history = useHistory();

  const onAction = (device: MyHardwareDevice, action: AssetActionKind): void => {
    if (action === "startReplacement") {
      const assetTag = device.assetTag || device.assetId;
      const navState: ReplacementNavState = {
        assetId: device.assetId,
        category: device.category,
        modelName: device.modelName,
      };
      resetReplacementDraft();
      history.push(buildPath(Paths.replacement.url, { assetTag }), navState);
      return;
    }
    history.push(Paths.orders.url);
  };

  if (isLoading) {
    return (
      <View display="flex" justifyContent="center" padding="S400" data-testid="my-hardware-loading">
        <Spinner size={SpinnerSize.Medium} />
      </View>
    );
  }

  if (isError) {
    return (
      <Row gridGap="S300" alignItems="center">
        <MessageBanner type={MessageBannerType.Error} isDismissible={false}>
          {getString("myHardware.loadError")}
        </MessageBanner>
        <Button variant={ButtonVariant.Secondary} onClick={() => refetch()}>
          {getString("myHardware.tryAgain")}
        </Button>
      </Row>
    );
  }

  const devices = data?.devices ?? [];

  if (devices.length === 0) {
    return (
      <EmptyState
        illustration="workStation"
        heading={getString("myHardware.noAssets.heading")}
        description={getString("myHardware.noAssets.description")}
        dataTestId="no-assets"
      />
    );
  }

  return (
    <Col gridGap="S500" width="100%">
      <DeviceGroup
        heading={getString("myHardware.actionNeeded")}
        devices={devices.filter((device) => needsAction(device.status))}
        isActionable
        onAction={onAction}
        dataTestId="group-action-needed"
      />
      <DeviceGroup
        heading={getString("myHardware.noActionNeeded")}
        devices={devices.filter((device) => !needsAction(device.status))}
        isActionable={false}
        onAction={onAction}
        dataTestId="group-no-action-needed"
      />
    </Col>
  );
};
